import type { ReactElement, ReactNode } from 'react';
import { Link } from '@/components/link';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '@/lib/currency';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import { cn } from '@/lib/utils';
import { getOrderDisplayStatus, getOrderStatusDisplayLabel, getOrderStatusConfigGeneral } from '@/lib/order-status';
import { SvgRight } from '@/components/icons-eataly';
import {
    ORDER_LIST_AMOUNT_CELL,
    ORDER_LIST_CARD_CLASS,
    ORDER_LIST_DATE_CELL,
    ORDER_LIST_ITEMS_CELL,
    ORDER_LIST_MOBILE_AMOUNT_CELL,
    ORDER_LIST_MOBILE_DATE_CELL,
    ORDER_LIST_MOBILE_ITEMS_CELL,
    ORDER_LIST_MOBILE_LAYOUT,
    ORDER_LIST_MOBILE_ROW_ACTION,
    ORDER_LIST_MOBILE_STATUS_CELL,
    ORDER_LIST_ROW_ACTION,
    ORDER_LIST_ROW_LAYOUT,
    ORDER_LIST_STATUS_CELL,
} from '@/components/account/order-list-item/order-list-layout';
import type { OrderListItemData } from '@/lib/order-display-types';
import { useLocation } from 'react-router';

export type { OrderListItemData, OrderProductItem, PickupLocation } from '@/lib/order-display-types';

/**
 * Props for the OrderListItem component.
 */
export interface OrderListItemProps {
    order: OrderListItemData;
    /** Optional callback when the order row is clicked (navigation is handled by the row link). */
    onViewDetails?: (orderNo: string) => void;
    /** Custom class name */
    className?: string;
}

/**
 * Format a date string for display using the current locale.
 */
function formatOrderDate(dateString: string, locale: string, invalidDateLabel: string): string {
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return invalidDateLabel;
        }
        return date.toLocaleDateString(locale, {
            month: 'numeric',
            day: 'numeric',
            year: 'numeric',
        });
    } catch {
        return invalidDateLabel;
    }
}

const ORDER_STATUS_BADGE_CLASS = 'max-w-min min-w-[120px] shrink whitespace-normal text-wrap text-center';

/**
 * Renders an order status badge.
 * Pass `status` as `externalOrderStatus ?? order.status` (or via {@link getOrderDisplayStatus}).
 * Known external tokens → colored Eataly badge; otherwise translated SCAPI / formatted fallback.
 */
export function OrderStatusBadge({ status, label }: { status?: string; label?: string }): ReactNode {
    const { t } = useTranslation('account');
    const displayLabel = getOrderStatusDisplayLabel(status, t, label);
    if (!displayLabel) {
        return null;
    }

    const config = getOrderStatusConfigGeneral(status);

    return (
        <Badge
            data-testid="order-status-badge"
            hierarchy={config?.badgeHierarchy ?? 'primary'}
            schema={config?.badgeSchema ?? 'default'}
            title={displayLabel}
            className={ORDER_STATUS_BADGE_CLASS}>
            {displayLabel}
        </Badge>
    );
}

/**
 * OrderListItem component displays a single order in a card format.
 */
export function OrderListItem({ order, onViewDetails, className }: OrderListItemProps): ReactElement {
    const { t, i18n } = useTranslation('account');
    const invalidDateLabel = t('orders.invalidDate');
    const { currency: siteCurrency } = useSite();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const queryString = params.toString();
    const orderDetailsUrl = queryString
        ? `${location.pathname}/${order.orderNo}?${queryString}`
        : `${location.pathname}/${order.orderNo}`;
    const formattedDate = formatOrderDate(order.orderDate, i18n.language, invalidDateLabel);
    const formattedTotal = formatCurrency(order.total, i18n.language, order.currency ?? siteCurrency);
    const isLegacy = order.source === 'legacy';
    const itemCountLabel = isLegacy ? order.orderNo : t('orders.itemCount', { count: order.productItemsCount });
    const legacyStatuses = t('orders.legacyStatuses', { returnObjects: true });
    const legacyStatusLabel =
        order.legacyStatus && Object.hasOwn(legacyStatuses, order.legacyStatus)
            ? legacyStatuses[order.legacyStatus as keyof typeof legacyStatuses]
            : order.legacyStatus;
    const status =
        getOrderDisplayStatus({
            externalOrderStatus: order.externalOrderStatus,
            status: order.status,
        }) ?? order.status;
    // Aria-label needs the resolved text outside the badge.
    const statusLabel = isLegacy ? legacyStatusLabel : getOrderStatusDisplayLabel(status, t, order.statusLabel);
    const rowLabel = t('orders.orderRowAriaLabel', {
        date: formattedDate,
        itemCount: itemCountLabel,
        amount: formattedTotal,
        status: statusLabel,
    });

    const content = (
        <>
            <div className={ORDER_LIST_MOBILE_LAYOUT} data-testid="order-list-item-mobile">
                <span className={ORDER_LIST_MOBILE_DATE_CELL}>{formattedDate}</span>
                <span className={ORDER_LIST_MOBILE_AMOUNT_CELL}>{formattedTotal}</span>
                {!isLegacy && <SvgRight className={ORDER_LIST_MOBILE_ROW_ACTION} aria-hidden />}
                <span className={ORDER_LIST_MOBILE_ITEMS_CELL}>{itemCountLabel}</span>
                <div className={ORDER_LIST_MOBILE_STATUS_CELL}>
                    <OrderStatusBadge status={isLegacy ? undefined : status} label={statusLabel} />
                </div>
            </div>

            {/* 2. ORIGINAL CARD DESKTOP (hidden on mobile, visible from 'sm' in up) */}
            <Card className={cn(ORDER_LIST_CARD_CLASS, 'hidden sm:block')} data-testid="order-list-item-desktop">
                <CardContent className={cn('p-0', ORDER_LIST_ROW_LAYOUT)}>
                    <span role="cell" className={cn(ORDER_LIST_DATE_CELL, 'body-copy-l-bold')}>
                        {formattedDate}
                    </span>
                    <span role="cell" className={cn(ORDER_LIST_ITEMS_CELL, 'body-copy-l')}>
                        {itemCountLabel}
                    </span>
                    <span role="cell" className={cn(ORDER_LIST_AMOUNT_CELL, 'body-copy-l')}>
                        {formattedTotal}
                    </span>
                    <div role="cell" className={ORDER_LIST_STATUS_CELL}>
                        <OrderStatusBadge status={isLegacy ? undefined : status} label={statusLabel} />
                    </div>
                    {!isLegacy && <SvgRight className={ORDER_LIST_ROW_ACTION} aria-hidden />}
                </CardContent>
            </Card>
        </>
    );

    return isLegacy ? (
        <div role="row" className={cn('block min-w-0', className)} aria-label={rowLabel}>
            {content}
        </div>
    ) : (
        <Link
            to={orderDetailsUrl}
            role="row"
            className={cn('block min-w-0', className)}
            onClick={() => onViewDetails?.(order.orderNo)}
            aria-label={rowLabel}>
            {content}
        </Link>
    );
}

export default OrderListItem;
